/**
 * useIdScan — orchestrator hook that composes useTwainBridge +
 * file-reader + camera-canvas capture into a single state machine,
 * plus runs `IdExtractionService` when the officer hits "Extract Fields".
 *
 * Ported from legacy SPFx `IdCapturePanel.tsx` lines 170-477 (state + handlers).
 *
 * State model:
 *   - `imageData` / `imageMime` / `imagePreviewUrl` — the currently captured image
 *   - `extracting` / `extracted` / `extractionError` — AI pipeline result
 *
 * Capture sources converge on `setImage(base64, mime)` so the downstream
 * preview + "Extract Fields" button is source-agnostic.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import {
  IdExtractionService,
  type ExtractedIdFields,
  type IdExtractionContext,
} from '../services/IdExtractionService.js';
import { SCAN_RECOVERY_ADVICE, useTwainBridge, type UseTwainBridgeResult } from './useTwainBridge.js';
import { preflightImageFile } from '../utils/imageConvert';

export interface UseIdScanResult {
  /** TWAIN bridge status + scan() (pass-through so callers don't need a 2nd hook). */
  twain: UseTwainBridgeResult;

  // Capture state ---------------------------------------------------------
  /** Base64 body only (no `data:...;base64,` prefix). */
  imageData: string | null;
  imageMime: string | null;
  /** `data:<mime>;base64,<body>` — safe to drop straight into `<img src=...>`. */
  imagePreviewUrl: string | null;

  // Extraction state ------------------------------------------------------
  extracting: boolean;
  extracted: ExtractedIdFields | null;
  extractionError: string | null;

  // Actions ---------------------------------------------------------------
  captureFromTwain: (source?: string) => Promise<void>;
  captureFromFile: (file: File) => Promise<void>;
  captureFromCamera: (canvas: HTMLCanvasElement) => Promise<void>;
  /** Manually set the image (used by sub-components that already have base64). */
  setImage: (base64: string, mimeType: string) => void;
  runExtraction: () => Promise<void>;
  reset: () => void;
}

/**
 * Convert a File to `{ base64, mimeType }`. Rejects on read error.
 */
function fileToBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const dataUrl = reader.result as string;
      const commaIdx = dataUrl.indexOf(',');
      const base64 = commaIdx >= 0 ? dataUrl.substring(commaIdx + 1) : dataUrl;
      resolve({ base64, mimeType: file.type || 'application/octet-stream' });
    };
    reader.onerror = (): void => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function useIdScan(context?: IdExtractionContext): UseIdScanResult {
  const apiClient = useApiClient();
  const twain = useTwainBridge();

  // Keep the latest provenance context in a ref so runExtraction reads fresh
  // values without re-memoizing every render (context is a new object literal
  // each render). Threaded into the scan so the DocumentExtract row links to
  // the cart/person instead of landing orphaned.
  const contextRef = useRef<IdExtractionContext | undefined>(context);
  contextRef.current = context;

  const [imageData, setImageData] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string | null>(null);
  const [extracting, setExtracting] = useState<boolean>(false);
  const [extracted, setExtracted] = useState<ExtractedIdFields | null>(null);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Lazy singleton service — re-created only when the api client changes.
  const service = useMemo(() => new IdExtractionService(apiClient), [apiClient]);

  const imagePreviewUrl = useMemo<string | null>(() => {
    if (!imageData || !imageMime) return null;
    return `data:${imageMime};base64,${imageData}`;
  }, [imageData, imageMime]);

  const setImage = useCallback((base64: string, mimeType: string): void => {
    setImageData(base64);
    setImageMime(mimeType);
    setExtracted(null);
    setExtractionError(null);
  }, []);

  const captureFromTwain = useCallback(
    async (source?: string): Promise<void> => {
      const result = await twain.scan(source);
      if (!result) {
        // CHG-0102: say why instead of showing nothing.
        const why = twain.lastScanError() ?? 'The scan did not complete.';
        setExtractionError(why.includes('Upload or Camera') ? why : `${why} ${SCAN_RECOVERY_ADVICE}`);
        return;
      }
      setImage(result.image, result.mimeType);
    },
    [twain, setImage],
  );

  const captureFromFile = useCallback(
    async (file: File): Promise<void> => {
      // Intake accepts up to 25 MB (SPFx parity — Bill 2026-07-13 Item 14);
      // preflightImageFile converts/downsamples (incl. oversize-PDF page-1
      // rasterization) so the OUTPUT still honors Azure DI's 4 MB hard cap.
      const INTAKE_MAX_BYTES = 25 * 1024 * 1024;
      const TARGET_MAX_BYTES = 4 * 1024 * 1024;
      if (file.size > INTAKE_MAX_BYTES) {
        setExtractionError(
          `File too large (max 25 MB). Got ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
        );
        return;
      }
      let prepared = file;
      if (file.size > TARGET_MAX_BYTES) {
        try {
          prepared = await preflightImageFile(file, TARGET_MAX_BYTES);
        } catch (err) {
          setExtractionError(
            err instanceof Error ? err.message : 'Could not resize the file — try a lower-resolution scan.',
          );
          return;
        }
      }
      const { base64, mimeType } = await fileToBase64(prepared);
      setImage(base64, mimeType);
    },
    [setImage],
  );

  const captureFromCamera = useCallback(
    async (canvas: HTMLCanvasElement): Promise<void> => {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const commaIdx = dataUrl.indexOf(',');
      const base64 = commaIdx >= 0 ? dataUrl.substring(commaIdx + 1) : dataUrl;
      setImage(base64, 'image/jpeg');
    },
    [setImage],
  );

  const runExtraction = useCallback(async (): Promise<void> => {
    if (!imageData || !imageMime) return;
    if (extracting) return;
    setExtracting(true);
    setExtractionError(null);
    setExtracted(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await service.extractFromImage(
        imageData, imageMime, controller.signal, contextRef.current);
      setExtracted(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setExtractionError(err instanceof Error ? err.message : String(err));
    } finally {
      setExtracting(false);
      abortRef.current = null;
    }
  }, [imageData, imageMime, extracting, service]);

  const reset = useCallback((): void => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
    setImageData(null);
    setImageMime(null);
    setExtracted(null);
    setExtractionError(null);
    setExtracting(false);
  }, []);

  return {
    twain,
    imageData,
    imageMime,
    imagePreviewUrl,
    extracting,
    extracted,
    extractionError,
    captureFromTwain,
    captureFromFile,
    captureFromCamera,
    setImage,
    runExtraction,
    reset,
  };
}
